import { useCallback, useState } from 'react';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { takeAttachmentFailures } from '@/lib/entity-attachments';

/** Minimum shape this hook needs from an "attachment" -- just enough to
 *  filter it out of `uploadedFiles` after a delete. Most modules' own
 *  attachment type is already `GenericAttachment` (the shape
 *  `GenericAttachmentsSection` renders), but a few (e.g. WarrantyAttachment)
 *  keep a different field layout and only map to `GenericAttachment` at
 *  the JSX boundary -- this hook works for either as long as `id` exists. */
export interface AttachmentLike {
  id: string;
}

/**
 * The one shared implementation of the "Create Lead" attachments pattern,
 * used by every creation modal in the app:
 *
 *   1. User picks files while the create modal is open.
 *   2. Files are held in memory (`pendingFiles`) -- nothing is uploaded yet.
 *   3. User can see/remove them before creating.
 *   4. On Create/Save, the record is created first and its id obtained.
 *   5. `uploadPending(newId)` (or the parent calling `uploadFn` directly
 *      with the staged files -- see below) uploads them against that id.
 *   6. On cancel, the files simply never existed anywhere but this hook's
 *      own state -- nothing to clean up server-side.
 *   7. No attachments -- `pendingFiles`/`uploadedFiles` just stay empty.
 *
 * Two call shapes exist across the app, both supported:
 *   - "Parent uploads": the form only stages files locally and reports
 *     `pendingFiles` back to its parent (e.g. via `attachmentsFiles` in the
 *     onSubmit payload); the PARENT creates the record and calls its own
 *     upload function with the new id. Use `pendingFiles` for this.
 *   - "Form uploads": the form itself calls the create API and gets the
 *     new record back. Call `uploadPending(newId)` right after that succeeds.
 *
 * Edit mode (a real `entityId` already exists) uploads immediately instead
 * of staging -- exactly like Create Lead's own edit-mode behavior.
 */
export interface UseAttachmentStagingOptions<T extends AttachmentLike = GenericAttachment> {
  /** The record's real id, once it exists. Falsy means "not created yet" --
   *  newly picked files are staged locally instead of uploaded immediately. */
  entityId?: string | null;
  /** Uploads `files` for an existing `entityId`. Used both for the
   *  immediate-upload edit-mode path and internally by `uploadPending`. */
  uploadFn: (entityId: string, files: File[]) => Promise<T[]>;
  /** Deletes an already-uploaded attachment by id. Omit if this record type
   *  doesn't support removing attachments once uploaded. */
  deleteFn?: (attachmentId: string) => Promise<boolean>;
  /** Seeds `uploadedFiles` on first mount (e.g. attachments already fetched
   *  by the parent for edit mode) -- avoids a one-frame flash of "no
   *  attachments" before a separate effect would otherwise populate them.
   *  Only used on mount; the form's own effect should still call
   *  `setUploadedFiles` when this data changes later (e.g. switching which
   *  record is being edited without unmounting). */
  initialUploadedFiles?: T[];
  /** Where newly-uploaded files land relative to the existing list in
   *  `uploadedFiles`. Defaults to 'append' (Create Lead's own order). Set
   *  to 'prepend' for a form whose existing UI shows newest-first. */
  newFilesPosition?: 'append' | 'prepend';
  /** Called with a user-facing message on an upload/delete failure. */
  onError?: (message: string) => void;
}

export interface UseAttachmentStagingResult<T extends AttachmentLike = GenericAttachment> {
  /** Files picked before the record existed, not yet uploaded anywhere. */
  pendingFiles: File[];
  /** Attachments that already exist server-side (fetched for edit mode, or
   *  the result of an upload). */
  uploadedFiles: T[];
  /** Escape hatch for forms that need to seed `uploadedFiles` from a fetched
   *  list themselves (e.g. inside their own existing "populate on open"
   *  effect) -- staging/upload/delete stays in the hook, but *when* to
   *  (re)fetch existing attachments is still each form's own concern. */
  setUploadedFiles: React.Dispatch<React.SetStateAction<T[]>>;
  /** Wire directly to `<GenericAttachmentsSection onUpload>`. */
  handleUpload: (files: File[]) => Promise<void>;
  /** Wire directly to `<GenericAttachmentsSection onRemovePending>`. */
  handleRemovePending: (index: number) => void;
  /** Wire directly to `<GenericAttachmentsSection onDelete>`. No-op
   *  (resolves false) if this record type has no `deleteFn`. Returns
   *  whether the delete actually succeeded, for callers that need to chain
   *  a side effect (e.g. notifying a parent) only on success. */
  handleDelete: (attachmentId: string) => Promise<boolean>;
  /** Clears staged files. Call when the modal (re)opens for a fresh create
   *  session -- otherwise files staged before a cancelled create would
   *  still be sitting in state the next time the modal opens. */
  resetPending: () => void;
  /** "Form uploads" call shape: uploads whatever is currently staged
   *  against a newly-created record's id, merges the result into
   *  `uploadedFiles`, and clears `pendingFiles`. Call right after the
   *  create API call succeeds. No-op (resolves to `[]`) if nothing is
   *  staged. */
  uploadPending: (newEntityId: string) => Promise<T[]>;
}

export function useAttachmentStaging<T extends AttachmentLike = GenericAttachment>({
  entityId,
  uploadFn,
  deleteFn,
  initialUploadedFiles,
  newFilesPosition = 'append',
  onError,
}: UseAttachmentStagingOptions<T>): UseAttachmentStagingResult<T> {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<T[]>(initialUploadedFiles ?? []);

  const mergeUploaded = useCallback((prev: T[], uploaded: T[]) => (
    newFilesPosition === 'prepend' ? [...uploaded, ...prev] : [...prev, ...uploaded]
  ), [newFilesPosition]);

  const handleUpload = useCallback(async (files: File[]) => {
    if (!files || files.length === 0) return;

    if (!entityId) {
      setPendingFiles((prev) => [...prev, ...files]);
      return;
    }

    try {
      const uploaded = await uploadFn(entityId, files);
      if (uploaded.length > 0) {
        setUploadedFiles((prev) => mergeUploaded(prev, uploaded));
      }
    } catch (error) {
      console.error('[useAttachmentStaging] upload failed', error);
      onError?.('Failed to upload files');
    }
  }, [entityId, uploadFn, onError, mergeUploaded]);

  const handleRemovePending = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDelete = useCallback(async (attachmentId: string): Promise<boolean> => {
    if (!deleteFn) return false;
    try {
      const ok = await deleteFn(attachmentId);
      if (ok) {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== attachmentId));
      }
      return ok;
    } catch (error) {
      console.error('[useAttachmentStaging] delete failed', error);
      onError?.('Failed to delete file');
      return false;
    }
  }, [deleteFn, onError]);

  const resetPending = useCallback(() => {
    setPendingFiles([]);
  }, []);

  const uploadPending = useCallback(async (newEntityId: string): Promise<T[]> => {
    if (pendingFiles.length === 0) return [];
    try {
      const uploaded = await uploadFn(newEntityId, pendingFiles);
      if (uploaded.length > 0) {
        setUploadedFiles((prev) => mergeUploaded(prev, uploaded));
      }
      setPendingFiles([]);
      // the record is saved but some files were refused: say which and why (the form decides where to show it)
      const failures = takeAttachmentFailures();
      if (failures.length > 0) onError?.(failures.join('\n'));
      return uploaded;
    } catch (error) {
      console.error('[useAttachmentStaging] upload-after-create failed', error);
      onError?.('Failed to upload files');
      return [];
    }
  }, [pendingFiles, uploadFn, onError, mergeUploaded]);

  return {
    pendingFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload,
    handleRemovePending,
    handleDelete,
    resetPending,
    uploadPending,
  };
}
